import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Card de KPI. Anatomia idêntica sempre — entre os cards de uma tela e entre
 * telas diferentes.
 *
 * **Estado nunca vira borda nem fundo do card.** Vai na `legenda`, como
 * `StatusBadge`. Era isso que fazia os quatro cards do topo de Vendas parecerem
 * quatro componentes diferentes: um com borda âmbar, um com fundo verde, um sem
 * nada e um com link dentro. O estado é a mesma informação — muda o tom do
 * badge, não a moldura.
 *
 * O valor é `whitespace-nowrap`: "R$ 87.138,60" quebrando em duas linhas foi
 * defeito real do protótipo, e um número partido ao meio não se lê de relance.
 * Por isso ele é `text-xl md:text-2xl`, e não `text-2xl` seco: em 390px a grade
 * é de duas colunas (~163px úteis cada) e um valor em reais com milhar não cabe
 * no corpo maior sem vazar. Mesma escala responsiva do título do `PageHeader`.
 *
 * **O card corta dentro de si (`overflow-hidden`), nunca invade o vizinho.**
 * Como o card seguinte tem fundo opaco (`bg-card`), o que vaza é PINTADO POR
 * CIMA — e aí o dígito que falta em "R$ 104.289,0" some sem deixar rastro,
 * corrompendo a leitura de um dado que nem é daquele card. Cortar é ruim;
 * invadir o vizinho é pior. O valor inteiro continua recuperável no `title`.
 */
export interface StatCardProps {
  /** Rótulo curto, em caixa alta. */
  rotulo: string;
  /** Já formatado — o card não formata número. */
  valor: string;
  /** Ao lado do valor, menor e atenuado. */
  unidade?: string;
  /** Terceira linha livre: StatusBadge, barra de progresso ou texto. */
  legenda?: React.ReactNode;
  className?: string;
  testId?: string;
}

export function StatCard({ rotulo, valor, unidade, legenda, className, testId }: StatCardProps) {
  return (
    <div
      className={cn("rounded-lg border bg-card p-3 min-w-0 overflow-hidden", className)}
      data-testid={testId}
    >
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground truncate">
        {rotulo}
      </p>
      <p className="mt-1 flex items-baseline gap-1 min-w-0 whitespace-nowrap">
        <span
          className="font-mono text-xl md:text-2xl font-semibold tabular-nums"
          title={valor}
        >
          {valor}
        </span>
        {unidade && <span className="text-xs text-muted-foreground truncate">{unidade}</span>}
      </p>
      {/* A legenda é frase, não número: ela QUEBRA. O pill "todo o colhido de
          06/08 a 21/08 está alocado" vazava 126px porque o Badge é
          `whitespace-nowrap` por padrão — aqui esse padrão é desfeito. */}
      {legenda && (
        <div className="mt-1.5 min-w-0 text-xs text-muted-foreground [&_*]:whitespace-normal break-words">
          {legenda}
        </div>
      )}
    </div>
  );
}

/**
 * A faixa de KPIs. Duas colunas no celular, quatro no desktop — o número de
 * cards não muda a largura de cada um entre telas.
 *
 * **Nunca receba cap de largura aqui.** Foi um `max-w-3xl` (768px) nesta grade,
 * em Vendas, que causou o bug dos KPIs vazando: a `main` tinha 1464px, a tabela
 * abaixo usava os 1464 inteiros, mas a grade ficava em 768 — 183px por card
 * para um valor de 187px. A faixa de KPIs acompanha a largura da superfície de
 * dados da tela, sempre.
 */
export function StatCardGrid({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3", className)}>
      {children}
    </div>
  );
}
